<script setup>
/**
 * The status cell as a control. The four workflow states write through
 * `toggleStatus`; Closed is a disposition, so it writes through
 * `assignResolution` - the same split the thread's state pill and move menu
 * already make. A ticket that is already shelved (closed / archive / spam /
 * trash) is restored from the row menu, so its pill stays static here.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import StatusPill from 'dashboard/components-next/Conversation/StatusPill.vue';
import CellMenu from './CellMenu.vue';
import CellMenuItem from './CellMenuItem.vue';
import {
  RESOLUTION,
  SENDABLE_STATES,
  STATE_META,
  STATUS_FOR_STATE,
  TICKET_STATE,
} from 'dashboard/helper/conversationLens';

const props = defineProps({
  conversationId: { type: Number, required: true },
  state: { type: String, required: true },
});

const { t } = useI18n();
const store = useStore();

const isWritable = computed(() => SENDABLE_STATES.includes(props.state));

const options = computed(() =>
  [...SENDABLE_STATES, TICKET_STATE.CLOSED].map(value => ({
    value,
    label: t(STATE_META[value].labelKey),
    icon: STATE_META[value].icon,
    iconClass: STATE_META[value].iconClass,
  }))
);

const setState = async (state, hide) => {
  hide();
  if (state === props.state) return;
  try {
    if (state === TICKET_STATE.CLOSED) {
      await store.dispatch('assignResolution', {
        conversationId: props.conversationId,
        resolution: RESOLUTION.CLOSED,
      });
      return;
    }
    await store.dispatch('toggleStatus', {
      conversationId: props.conversationId,
      status: STATUS_FOR_STATE[state],
    });
  } catch (error) {
    useAlert(t('TICKETS.TABLE.CELL.ERROR'));
  }
};
</script>

<template>
  <StatusPill v-if="!isWritable" :state="state" />
  <CellMenu v-else>
    <template #trigger="{ isOpen }">
      <button
        type="button"
        data-testid="status-trigger"
        aria-haspopup="menu"
        :aria-expanded="isOpen"
        :aria-label="t('TICKETS.TABLE.CELL.CHANGE_STATUS')"
        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap"
        :class="STATE_META[state].pillClass"
      >
        <span :class="STATE_META[state].icon" class="size-3 shrink-0" />
        {{ t(STATE_META[state].labelKey) }}
        <span
          class="i-lucide-chevron-down size-3 shrink-0 opacity-0 group-hover/row:opacity-100"
          :class="isOpen ? 'opacity-100' : ''"
        />
      </button>
    </template>
    <template #default="{ hide }">
      <CellMenuItem
        v-for="option in options"
        :key="option.value"
        role="menuitemradio"
        :data-testid="`status-option-${option.value}`"
        :label="option.label"
        :icon="option.icon"
        :icon-class="option.iconClass"
        :selected="option.value === state"
        @click="setState(option.value, hide)"
      />
    </template>
  </CellMenu>
</template>
